// The ONE pickup-order array every order view in the ops demo derives from:
// the outlet's 180 on-demand pickups (core story) plus one order per
// collected or cancelled stop of the recycler's route runs (60 days + today).
// Outlet lists, driver requests, trips, collections, approvals, facility
// intake, driver history, pre-order fulfilment and evidence pages all read
// these rows, so their numbers reconcile.

import { PickupOrderFillLevel } from '../../types/order.type';
import {
  DEMO_COLLECTION_POINTS,
  DEMO_CONTAINERS,
  DEMO_DRIVERS,
  DEMO_FACILITIES,
  DEMO_MATERIAL_IDS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_ORG_ID,
  DEMO_ROUTES,
  DEMO_SITES,
  DEMO_SUPPLIERS,
  DEMO_TRUCKS,
  DEMO_USER_ID,
  HOUR_MS,
  generatePickupOrders,
  generateRouteRuns,
  hashString,
  ids,
  iso,
  isoDate,
  runPosition,
  startOfToday,
  type DemoRouteRun,
} from '../core';
import { makeCounter, nowIso, round2, unit } from './common';
import { agreementPriceOn } from './network';
import { estimatedKgForFill, markContainerEmptied } from './sites';

export type OpsOrderStatus = 'pending' | 'in_transit' | 'completed' | 'cancelled';
export type OpsApproval = 'pending' | 'approved' | 'rejected';

export interface PickupHandshake {
  status: 'approved' | 'pending' | 'rejected' | 'superseded';
  approver_name: string;
  weight_kg: number;
  responded_at: string | null;
  created_at: string;
  rejection_note: string | null;
}

export interface OpsOrder {
  id: string;
  /** Stable small integer used to derive related ids (assignments, deliveries). */
  seq: number;
  source: 'on_demand' | 'route_run';
  reference: string;
  /** Organization that owns the pickup order. */
  organization_id: string;
  /** The Recyclee waste outlet the material came from, when there is one. */
  outlet_org_id: string | null;
  recycler_org_id: string;
  site_id: string | null;
  container_id: string | null;
  collection_point_id: string | null;
  supplier_id: string | null;
  material_id: string;
  status: OpsOrderStatus;
  created_at: string;
  updated_at: string;
  scheduled_at: string;
  picked_up_at: string | null;
  completed_at: string | null;
  cancelled_at: string | null;
  cancellation_reason: string | null;
  estimated_kg: number;
  verified_kg: number | null;
  approval_status: OpsApproval | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  rejection_reason_ar: string | null;
  sale_amount: number | null;
  weight_corrected_by_user_id: string | null;
  flags: string[];
  facility_id: string | null;
  truck_id: string | null;
  driver_id: string | null;
  route_run_id: string | null;
  route_id: string | null;
  stop_seq: number | null;
  fill_level: PickupOrderFillLevel | null;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  notes: string;
  special_instructions: string;
  pickup_approval: PickupHandshake | null;
  invoice_serial: string | null;
}

export interface OpsRunStop {
  id: string;
  collection_point_id: string;
  supplier_id: string;
  sequence: number;
  status: 'pending' | 'arrived' | 'collected' | 'cancelled';
  pickup_order_id: string | null;
  material_id: string;
  arrived_at: string | null;
  completed_at: string | null;
  cancellation_reason: string | null;
  cancellation_notes: string | null;
  cancelled_at: string | null;
}

export interface OpsRun {
  id: string;
  number: number;
  route_id: string;
  truck_id: string;
  driver_id: string | null;
  facility_id: string;
  run_date: string;
  status: 'pending' | 'in_progress' | 'completed';
  scheduled_start_at: string;
  clock_in_at: string | null;
  clock_out_at: string | null;
  offload_at: string | null;
  distance_km: number;
  duration_seconds: number;
  geometry: Array<[number, number]>;
  stops: OpsRunStop[];
  core: DemoRouteRun;
}

let orders: OpsOrder[] = [];
let runs: OpsRun[] = [];
const createdCounter = makeCounter(0);

const ROUTE_ORDER_BASE = 0x800000;
const NEW_ORDER_BASE = 0x400000;

const STOP_CANCEL_REASONS: Array<[string, string]> = [
  ['site_closed', 'Gate locked on arrival; security had no instructions.'],
  ['no_waste_available', 'Bins emptied by the building cleaning contractor earlier in the day.'],
  ['access_blocked', 'Service road closed for maintenance works.'],
  ['contact_unreachable', 'Point contact did not answer after three calls.'],
  ['truck_full', 'Truck reached capacity before this stop.'],
];

const REJECTION_REASONS: Array<[string, string]> = [
  ['Load contaminated with food waste; cannot be baled.', 'الحمولة ملوثة ببقايا الطعام ولا يمكن كبسها.'],
  ['Cardboard was wet and mouldy on arrival.', 'الكرتون كان مبللاً ومتعفناً عند الوصول.'],
  ['Mixed with general waste above the 10% tolerance.', 'مختلط بنفايات عامة بنسبة تتجاوز 10%.'],
];

const OUTLET_CANCEL_REASONS = [
  'Cancelled by the branch: bin was emptied during the weekend clean-up.',
  'Duplicate request raised by two shifts.',
  'Branch closed for refurbishment; pickup no longer needed.',
  'Cancelled by the branch: volume too small to justify a trip.',
];

function fillBand(pct: number): PickupOrderFillLevel {
  if (pct >= 90) return PickupOrderFillLevel.FULL;
  if (pct >= 65) return PickupOrderFillLevel.THREE_QT_FULL;
  if (pct >= 40) return PickupOrderFillLevel.HALF_FULL;
  return PickupOrderFillLevel.QT_FULL;
}

function minIso(a: string, b: string): string {
  return a < b ? a : b;
}

function plus(isoValue: string, ms: number): string {
  return iso(new Date(Date.parse(isoValue) + ms));
}

function notFuture(isoValue: string): string {
  return minIso(isoValue, nowIso());
}

function facilityCode(facilityId: string | null): string {
  return facilityId === DEMO_FACILITIES[1].id ? 'JED' : 'RUH';
}

function invoiceSerial(facilityId: string | null, completedAt: string, seq: number): string {
  return `WB-${facilityCode(facilityId)}-${completedAt.slice(2, 10).replace(/-/g, '')}-${String(seq % 10000).padStart(4, '0')}`;
}

/** Sale value for a weighed load at the agreement price that applied on the day. */
export function priceLoad(supplierId: string | null, materialId: string, kg: number, when: string): { amount: number; missing: boolean } {
  const price = agreementPriceOn(supplierId, materialId, when);
  if (price == null) return { amount: 0, missing: true };
  return { amount: round2(kg * price), missing: false };
}

function siteManager(siteId: string | null): string {
  return DEMO_SITES.find((s) => s.id === siteId)?.manager_name ?? 'Branch manager';
}

// ── On-demand pickups (the outlet's story) ─────────────────────────────────

function buildOnDemand(): OpsOrder[] {
  const supplierId = DEMO_SUPPLIERS[0].id;
  return generatePickupOrders().map((o, i) => {
    const seq = i + 1;
    const point = DEMO_COLLECTION_POINTS.find((p) => p.site_id === o.site_id);
    const status: OpsOrderStatus = o.status === 'accepted' ? 'pending' : o.status;
    const key = `po:${o.id}`;
    const pickedUp =
      status === 'completed' && o.completed_at
        ? plus(o.completed_at, -(1 + unit(key + 'p') * 1.5) * HOUR_MS)
        : status === 'in_transit'
          ? notFuture(plus(o.scheduled_at, 20 * 60000))
          : null;
    const cancelledAt = status === 'cancelled' ? notFuture(plus(o.created_at, (3 + unit(key + 'c') * 30) * HOUR_MS)) : null;
    const approvedAt =
      o.approval_status === 'approved' && o.completed_at ? notFuture(plus(o.completed_at, (2 + unit(key + 'a') * 20) * HOUR_MS)) : null;
    const rejected = o.approval_status === 'rejected';
    const reason = rejected ? REJECTION_REASONS[hashString(key) % REJECTION_REASONS.length] : null;
    const handshake: PickupHandshake | null =
      (status === 'completed' || status === 'in_transit') && unit(key + 'h') < 0.9
        ? {
            status: 'approved',
            approver_name: siteManager(o.site_id),
            weight_kg: o.estimated_kg,
            responded_at: pickedUp,
            created_at: pickedUp ? plus(pickedUp, -6 * 60000) : o.created_at,
            rejection_note: null,
          }
        : null;
    const isDc = DEMO_SITES.find((s) => s.id === o.site_id)?.site_type === 'distribution_center';
    const priority: OpsOrder['priority'] =
      o.fill_level >= 97 ? 'urgent' : isDc || o.fill_level >= 92 ? 'high' : unit(key + 'q') < 0.15 ? 'low' : 'medium';
    return {
      id: o.id,
      seq,
      source: 'on_demand',
      reference: o.reference,
      organization_id: DEMO_OUTLET_ORG_ID,
      outlet_org_id: DEMO_OUTLET_ORG_ID,
      recycler_org_id: DEMO_RECYCLER_ORG_ID,
      site_id: o.site_id,
      container_id: o.container_id,
      collection_point_id: point?.id ?? null,
      supplier_id: supplierId,
      material_id: o.material_id,
      status,
      created_at: o.created_at,
      updated_at: approvedAt ?? o.completed_at ?? cancelledAt ?? pickedUp ?? o.created_at,
      scheduled_at: o.scheduled_at,
      picked_up_at: pickedUp,
      completed_at: o.completed_at,
      cancelled_at: cancelledAt,
      cancellation_reason: status === 'cancelled' ? OUTLET_CANCEL_REASONS[hashString(key) % OUTLET_CANCEL_REASONS.length] : null,
      estimated_kg: o.estimated_kg,
      verified_kg: o.verified_kg,
      approval_status: o.approval_status,
      approved_at: approvedAt,
      rejected_at: rejected && o.completed_at ? notFuture(plus(o.completed_at, 5 * HOUR_MS)) : null,
      rejection_reason: reason?.[0] ?? null,
      rejection_reason_ar: reason?.[1] ?? null,
      sale_amount: o.sale_amount_sar,
      weight_corrected_by_user_id: null,
      flags: [],
      facility_id: o.facility_id,
      truck_id: o.truck_id,
      driver_id: o.driver_id,
      route_run_id: null,
      route_id: null,
      stop_seq: null,
      fill_level: fillBand(o.fill_level),
      priority,
      notes: unit(key + 'n') < 0.35 ? 'Bales are strapped and staged on pallets.' : '',
      special_instructions: isDc ? 'Use dock 4; driver needs a gate pass from security.' : '',
      pickup_approval: handshake,
      invoice_serial: status === 'completed' && o.completed_at ? invoiceSerial(o.facility_id, o.completed_at, seq) : null,
    };
  });
}

// ── Route runs and their collections ───────────────────────────────────────

function runNumber(runId: string): number {
  return Number.parseInt(runId.slice(-6), 16);
}

const POINT_POOLS: Record<number, string[]> = {
  7: ['occ', 'pet', 'aluminium_cans', 'uco', 'clear_glass'],
  8: ['occ', 'office_paper', 'pp'],
  9: ['occ', 'hdpe', 'pp'],
  10: ['occ', 'ldpe_film', 'pet', 'mixed_paper', 'aluminium_cans'],
  11: ['mixed_paper', 'newspaper', 'pet', 'occ'],
  12: ['office_paper', 'mixed_paper', 'pet'],
};

/** The stream a stop collects: one of the point's bins (synced sites) or its agreed materials. */
function stopMaterial(pointId: string, key: string): string {
  const point = DEMO_COLLECTION_POINTS.find((p) => p.id === pointId);
  if (point?.site_id) {
    const bins = DEMO_CONTAINERS.filter((c) => c.site_id === point.site_id);
    return bins[hashString(key) % bins.length].material_id;
  }
  const n = DEMO_COLLECTION_POINTS.findIndex((p) => p.id === pointId) + 1;
  const pool = POINT_POOLS[n] ?? ['occ'];
  return DEMO_MATERIAL_IDS[pool[hashString(key) % pool.length]];
}

function containerFor(siteId: string | null | undefined, materialId: string): string | null {
  if (!siteId) return null;
  return DEMO_CONTAINERS.find((c) => c.site_id === siteId && c.material_id === materialId)?.id ?? null;
}

function buildRuns(): { runs: OpsRun[]; orders: OpsOrder[] } {
  const today = isoDate(startOfToday());
  const outRuns: OpsRun[] = [];
  const outOrders: OpsOrder[] = [];
  generateRouteRuns().forEach((core) => {
    const route = DEMO_ROUTES.find((r) => r.id === core.route_id)!;
    const number = runNumber(core.id);
    const routeIdx = DEMO_ROUTES.indexOf(route) + 1;
    const offloadAt = core.finished_at ? plus(core.finished_at, -20 * 60000) : null;
    const stops: OpsRunStop[] = core.stops.map((s) => {
      const key = `${core.id}:${s.seq}`;
      const point = DEMO_COLLECTION_POINTS.find((p) => p.id === s.collection_point_id)!;
      const cancelled = core.status === 'completed' && core.run_date !== today && unit(key + 'x') < 0.045;
      const reason = cancelled ? STOP_CANCEL_REASONS[hashString(key) % STOP_CANCEL_REASONS.length] : null;
      const collected = s.status === 'completed' && !cancelled;
      return {
        id: ids.schedule(0x100000 + (number % 0x1ffff) * 8 + s.seq),
        collection_point_id: s.collection_point_id,
        supplier_id: point.supplier_id,
        sequence: s.seq,
        status: cancelled ? 'cancelled' : collected ? 'collected' : 'pending',
        pickup_order_id: collected || cancelled ? ids.pickupOrder(ROUTE_ORDER_BASE + (number % 0x7ffff) * 8 + s.seq) : null,
        material_id: stopMaterial(s.collection_point_id, key),
        arrived_at: s.arrived_at,
        completed_at: collected && s.arrived_at ? plus(s.arrived_at, (11 + unit(key + 'd') * 9) * 60000) : null,
        cancellation_reason: reason?.[0] ?? null,
        cancellation_notes: reason?.[1] ?? null,
        cancelled_at: cancelled ? s.arrived_at : null,
      };
    });
    const run: OpsRun = {
      id: core.id,
      number,
      route_id: core.route_id,
      truck_id: core.truck_id,
      driver_id: core.driver_id,
      facility_id: route.facility_id,
      run_date: core.run_date,
      status: core.status,
      scheduled_start_at: core.scheduled_start_at,
      clock_in_at: core.started_at,
      clock_out_at: core.finished_at,
      offload_at: offloadAt,
      distance_km: core.distance_km,
      duration_seconds: route.est_duration_min * 60,
      geometry: core.geometry,
      stops,
      core,
    };
    outRuns.push(run);

    core.stops.forEach((s, i) => {
      const stop = stops[i];
      if (!stop.pickup_order_id) return;
      const key = `${core.id}:${s.seq}`;
      const point = DEMO_COLLECTION_POINTS.find((p) => p.id === s.collection_point_id)!;
      const supplier = DEMO_SUPPLIERS.find((x) => x.id === point.supplier_id)!;
      const seq = 1000 + (number % 0x7ffff) * 8 + s.seq;
      const cancelled = stop.status === 'cancelled';
      const delivered = !cancelled && core.status === 'completed';
      const collectedKg = s.collected_kg ?? 0;
      const estimated = Math.max(20, Math.round((collectedKg * (0.9 + unit(key + 'e') * 0.2)) / 5) * 5);
      let approval: OpsApproval | null = null;
      if (delivered) approval = core.run_date === today ? 'pending' : unit(key + 'r') < 0.04 ? 'rejected' : 'approved';
      const verified = delivered ? collectedKg : null;
      const completedAt = delivered ? offloadAt : null;
      const priced = verified != null && completedAt ? priceLoad(supplier.id, stop.material_id, verified, completedAt) : null;
      const reason = approval === 'rejected' ? REJECTION_REASONS[hashString(key) % REJECTION_REASONS.length] : null;
      const handshake: PickupHandshake | null =
        !cancelled && unit(key + 'h') < 0.88
          ? {
              status: 'approved',
              approver_name: point.site_id ? siteManager(point.site_id) : supplier.contact_name,
              weight_kg: estimated,
              responded_at: stop.completed_at,
              created_at: stop.arrived_at ?? core.scheduled_start_at,
              rejection_note: null,
            }
          : null;
      const fill = 55 + Math.round(unit(key + 'f') * 45);
      outOrders.push({
        id: stop.pickup_order_id,
        seq,
        source: 'route_run',
        reference: `RR-${core.run_date.replace(/-/g, '').slice(2)}-${routeIdx}${s.seq}`,
        organization_id: DEMO_RECYCLER_ORG_ID,
        outlet_org_id: supplier.linked_organization_id,
        recycler_org_id: DEMO_RECYCLER_ORG_ID,
        site_id: point.site_id,
        container_id: containerFor(point.site_id, stop.material_id),
        collection_point_id: point.id,
        supplier_id: supplier.id,
        material_id: stop.material_id,
        status: cancelled ? 'cancelled' : delivered ? 'completed' : 'in_transit',
        created_at: core.started_at ?? core.scheduled_start_at,
        updated_at: completedAt ?? stop.cancelled_at ?? stop.completed_at ?? core.scheduled_start_at,
        scheduled_at: core.scheduled_start_at,
        picked_up_at: stop.completed_at,
        completed_at: completedAt,
        cancelled_at: stop.cancelled_at,
        cancellation_reason: stop.cancellation_notes,
        estimated_kg: cancelled ? 0 : estimated,
        verified_kg: verified,
        approval_status: approval,
        approved_at: approval === 'approved' && completedAt ? plus(completedAt, (1 + unit(key + 'a') * 18) * HOUR_MS) : null,
        rejected_at: approval === 'rejected' && completedAt ? plus(completedAt, 3 * HOUR_MS) : null,
        rejection_reason: reason?.[0] ?? null,
        rejection_reason_ar: reason?.[1] ?? null,
        sale_amount: approval === 'approved' ? priced?.amount ?? 0 : approval === 'rejected' ? 0 : null,
        weight_corrected_by_user_id: null,
        flags: priced?.missing ? ['missing_agreement_price'] : [],
        facility_id: route.facility_id,
        truck_id: core.truck_id,
        driver_id: core.driver_id,
        route_run_id: core.id,
        route_id: core.route_id,
        stop_seq: s.seq,
        fill_level: cancelled ? null : fillBand(fill),
        priority: 'medium',
        notes: '',
        special_instructions: '',
        pickup_approval: handshake,
        invoice_serial: completedAt ? invoiceSerial(route.facility_id, completedAt, seq) : null,
      });
    });
  });
  return { runs: outRuns, orders: outOrders };
}

// ── State ──────────────────────────────────────────────────────────────────

export function resetOrders(): void {
  createdCounter.reset();
  const built = buildRuns();
  runs = built.runs;
  orders = [...buildOnDemand(), ...built.orders];
}

export function allOrders(): OpsOrder[] {
  return orders;
}

export function orderById(id: string | null | undefined): OpsOrder | undefined {
  return orders.find((o) => o.id === id);
}

export function allRuns(): OpsRun[] {
  return runs;
}

export function runById(id: string | null | undefined): OpsRun | undefined {
  return runs.find((r) => r.id === id);
}

/** Where a run's truck is right now (moves along the route while the run is live). */
export function runTruckPosition(run: OpsRun): { lat: number; lng: number } {
  return runPosition(run.core);
}

/** Bins with a fresh (last 5 days) pending pickup request show "awaiting pickup". */
export function containersAwaitingPickup(): Set<string> {
  const since = iso(new Date(Date.now() - 5 * 24 * HOUR_MS));
  return new Set(
    orders
      .filter((o) => o.source === 'on_demand' && o.status === 'pending' && o.container_id && o.created_at >= since)
      .map((o) => o.container_id as string),
  );
}

// ── Mutations ──────────────────────────────────────────────────────────────

/** POST /pickup_order: a new on-demand request (always pending; dispatch offers it to a driver). */
export function createOrder(input: Record<string, unknown>): OpsOrder {
  const k = createdCounter.next();
  const data = (input.data ?? {}) as Record<string, unknown>;
  const siteId = (data.pickup_address as string | undefined) ?? null;
  const containerId = (data.container_id as string | undefined) ?? null;
  const fillLevel = (data.fill_level as PickupOrderFillLevel | undefined) ?? null;
  const site = DEMO_SITES.find((s) => s.id === siteId);
  const point = DEMO_COLLECTION_POINTS.find((p) => p.site_id === siteId);
  const amount = Number(input.amount ?? 0);
  const now = nowIso();
  const order: OpsOrder = {
    id: ids.pickupOrder(NEW_ORDER_BASE + k),
    seq: 500 + k,
    source: 'on_demand',
    reference: `PO-${now.slice(0, 4)}-${String(180 + k).padStart(4, '0')}`,
    organization_id: String(input.organization_id ?? DEMO_OUTLET_ORG_ID),
    outlet_org_id: String(input.organization_id ?? DEMO_OUTLET_ORG_ID),
    recycler_org_id: DEMO_RECYCLER_ORG_ID,
    site_id: siteId,
    container_id: containerId,
    collection_point_id: point?.id ?? null,
    supplier_id: point?.supplier_id ?? (site ? DEMO_SUPPLIERS[0].id : null),
    material_id: String(input.material_type_id ?? DEMO_MATERIAL_IDS.occ),
    status: 'pending',
    created_at: now,
    updated_at: now,
    scheduled_at: plus(now, 26 * HOUR_MS),
    picked_up_at: null,
    completed_at: null,
    cancelled_at: null,
    cancellation_reason: null,
    estimated_kg: amount > 0 ? amount : estimatedKgForFill(containerId, fillLevel),
    verified_kg: null,
    approval_status: null,
    approved_at: null,
    rejected_at: null,
    rejection_reason: null,
    rejection_reason_ar: null,
    sale_amount: null,
    weight_corrected_by_user_id: null,
    flags: [],
    facility_id: null,
    truck_id: null,
    driver_id: null,
    route_run_id: null,
    route_id: null,
    stop_seq: null,
    fill_level: fillLevel,
    priority: fillLevel === PickupOrderFillLevel.FULL ? 'high' : 'medium',
    notes: String(data.notes ?? ''),
    special_instructions: String(data.special_instructions ?? ''),
    pickup_approval: null,
    invoice_serial: null,
  };
  orders.push(order);
  return order;
}

/** PUT /pickup_order/{id}. Status moves stamp the matching timestamps. */
export function updateOrder(id: string, patch: Record<string, unknown>): OpsOrder | undefined {
  const order = orderById(id);
  if (!order) return undefined;
  const now = nowIso();
  if (typeof patch.material_type_id === 'string') order.material_id = patch.material_type_id;
  if (patch.amount !== undefined && Number(patch.amount) > 0) order.estimated_kg = Number(patch.amount);
  if (patch.data && typeof patch.data === 'object') {
    const d = patch.data as Record<string, unknown>;
    if (typeof d.pickup_address === 'string') order.site_id = d.pickup_address;
    if (typeof d.container_id === 'string') order.container_id = d.container_id;
    if (typeof d.fill_level === 'string') order.fill_level = d.fill_level as PickupOrderFillLevel;
    if (typeof d.notes === 'string') order.notes = d.notes;
    if (typeof d.special_instructions === 'string') order.special_instructions = d.special_instructions;
    if (typeof d.priority === 'string') order.priority = d.priority as OpsOrder['priority'];
  }
  if (typeof patch.status === 'string' && patch.status !== order.status) {
    const next = patch.status as OpsOrderStatus;
    order.status = next;
    if (next === 'cancelled') {
      order.cancelled_at = now;
      order.cancellation_reason = order.cancellation_reason ?? 'Cancelled by the outlet.';
    }
    if (next === 'in_transit') {
      order.picked_up_at = now;
      order.facility_id = order.facility_id ?? (DEMO_SITES.find((s) => s.id === order.site_id)?.city === 'Jeddah' ? DEMO_FACILITIES[1].id : DEMO_FACILITIES[0].id);
    }
    if (next === 'completed') {
      order.completed_at = now;
      order.verified_kg = order.verified_kg ?? order.estimated_kg;
      order.approval_status = 'pending';
      order.invoice_serial = invoiceSerial(order.facility_id, now, order.seq);
      markContainerEmptied(order.container_id);
    }
  }
  if (patch.sale_amount !== undefined && order.approval_status === 'approved') order.sale_amount = Number(patch.sale_amount);
  order.updated_at = now;
  return order;
}

export function deleteOrder(id: string): boolean {
  const before = orders.length;
  orders = orders.filter((o) => o.id !== id);
  return orders.length !== before;
}

/** Facility verifies the weight: approved, priced from the supplier agreement. */
export function approveOrder(id: string, correctedKg?: number): OpsOrder | 'not_pending' | undefined {
  const order = orderById(id);
  if (!order) return undefined;
  if (order.approval_status !== 'pending') return 'not_pending';
  const now = nowIso();
  const handshakeSigned = order.pickup_approval?.status === 'approved';
  if (correctedKg != null && Number.isFinite(correctedKg) && correctedKg > 0 && !handshakeSigned) {
    order.verified_kg = round2(correctedKg);
    order.weight_corrected_by_user_id = DEMO_USER_ID;
  }
  const kg = order.verified_kg ?? order.estimated_kg;
  order.verified_kg = kg;
  const priced = priceLoad(order.supplier_id, order.material_id, kg, order.completed_at ?? now);
  order.sale_amount = priced.amount;
  order.flags = priced.missing ? ['missing_agreement_price'] : [];
  order.approval_status = 'approved';
  order.approved_at = now;
  order.updated_at = now;
  return order;
}

/** Facility refuses the load: sale drops to zero and the reason is kept. */
export function rejectOrder(id: string, reason: string): OpsOrder | 'not_pending' | undefined {
  const order = orderById(id);
  if (!order) return undefined;
  if (order.approval_status !== 'pending') return 'not_pending';
  const now = nowIso();
  order.approval_status = 'rejected';
  order.rejected_at = now;
  order.rejection_reason = reason;
  order.rejection_reason_ar = reason;
  order.sale_amount = 0;
  order.updated_at = now;
  return order;
}

// ── Lookups shared by the views ────────────────────────────────────────────

export function driverRecord(id: string | null | undefined) {
  return DEMO_DRIVERS.find((d) => d.id === id);
}

export function coreTruck(id: string | null | undefined) {
  return DEMO_TRUCKS.find((t) => t.id === id);
}

/** Day an order belongs to for date filters (the run day for route collections). */
export function orderDay(o: OpsOrder): string {
  if (o.route_run_id) return runById(o.route_run_id)?.run_date ?? (o.completed_at ?? o.created_at).slice(0, 10);
  return (o.completed_at ?? o.created_at).slice(0, 10);
}
